import { useEffect, useState, useRef } from 'react'
import useWorksCount from '@pc/components/Works/Panel/hooks/useWorksCount'
import SearchButton from '@pc/components/Works/Panel/shared/Button/SearchButton'
import WorksCount from '@pc/components/Works/Panel/shared/WorksCount'
import type { SearchParamsAttributes, Prefecture } from '@pc/components/Works/Panel/types'
import { buildDynamicSearchApiUrl } from '@pc/components/Works/Panel/utils/workSearchApi'
import AreaBox from './AreaSelectModal/AreaBox'
import AreaPanelButton from './AreaSelectModal/AreaPanelButton'
import PanelButtonPopupIcon from './PanelButtonPopupIcon'
import ModalCloseButton from './shared/ModalCloseButton'
import ModalFooter from './shared/ModalFooter'
import ModalHeader from './shared/ModalHeader'
import ModalInner from './shared/ModalInner'
import ModalTitle from './shared/ModalTitle'
import SelectModalDialog from './shared/SelectModalDialog'
import type { MouseEvent } from 'react'

interface PropsType {
  params: SearchParamsAttributes
  prefecture: Prefecture
  cities: Prefecture[]
}

const GUIDE_AREA_URL = '/prefectures'

const AreaSelectModal = (props: PropsType) => {
  const { params, prefecture, cities } = props

  const [modalIsOpen, setModalIsOpen] = useState(false)
  const [selectedCityIds, setSelectedCityIds] = useState<string[]>([])
  const { worksCount, updateCountFromParams } = useWorksCount()
  const renderFlgRef = useRef(false)

  const toggleModal = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    setModalIsOpen(!modalIsOpen)
  }

  const closeModal = () => {
    setModalIsOpen(false)
  }

  const changeCityIds = (cityIds: string[]) => {
    setSelectedCityIds(cityIds)
  }

  const mergedParams = () => {
    // 地域系の検索の場合は路線・駅の条件をリセットする仕様になっている
    // 検索パネルの仕様上、prefectureを変更することができず情報を保持していると検索結果がおかしくなる可能性があるためリセット
    return { ...params, city_ids: selectedCityIds, line_ids: [], lines_ids: [], station_ids: [], stations_ids: [] }
  }

  // ブランド一覧などブランドを1件に絞っている文脈では、都道府県を選び直してもブランド条件を引き継げるよう
  // 遷移先の都道府県一覧URLに brand_id を付与する（複数ブランド・ブランドなしの場合は通常の都道府県一覧へ）
  const guideAreaUrl = () => {
    const brandIds = params.brand_ids
    if (brandIds?.length === 1) {
      return `${GUIDE_AREA_URL}?brand_id=${brandIds[0]}`
    }
    return GUIDE_AREA_URL
  }

  useEffect(() => {
    if (renderFlgRef.current) {
      const mergedParams = { ...params, city_ids: selectedCityIds, line_ids: [], lines_ids: [], station_ids: [], stations_ids: [] }
      updateCountFromParams(mergedParams)
    } else {
      renderFlgRef.current = true
    }
  }, [selectedCityIds, params, updateCountFromParams])

  useEffect(() => {
    const paramCityCompatIds = params.city_ids
    const selectedCityIds: string[] = cities
      .filter((city) => paramCityCompatIds?.includes(city.attributes.compat_id.toString()))
      .map((city) => city.attributes.compat_id.toString())
    setSelectedCityIds(selectedCityIds)
  }, [cities, params.city_ids])

  return (
    <>
      <AreaPanelButton onClick={toggleModal}>
        エリア変更
        <PanelButtonPopupIcon />
      </AreaPanelButton>
      <SelectModalDialog isOpen={modalIsOpen} onRequestClose={closeModal}>
        <ModalInner>
          <ModalCloseButton onClick={toggleModal} />
          <ModalHeader>
            <ModalTitle>{prefecture.attributes.name}内のエリア変更</ModalTitle>
            <a href={guideAreaUrl()}>都道府県を変更</a>
          </ModalHeader>
          <AreaBox prefecture={prefecture} cities={cities} selectedCityIds={selectedCityIds} changeCityIds={changeCityIds} />
          <ModalFooter>
            <div className="flex items-center justify-end">
              <WorksCount count={worksCount} />
              <SearchButton searchUrl={buildDynamicSearchApiUrl(mergedParams())} />
            </div>
          </ModalFooter>
        </ModalInner>
      </SelectModalDialog>
    </>
  )
}

export default AreaSelectModal
